import Vue from 'vue'
import {
  ADD_ATTRIBUTE,
  UPDATE_ATTRIBUTE,
  SET_TARGET_HAD_CHANGED,
  UPDATE_OPERATOR,
  REMOVE_ATTRIBUTE,
  SELECT_ATTRIBUTE,
  CLEAR_SELECTION,
  GROUP,
  UNGROUP,
  CLEAR_DEFINITION,
  SET_DEFINITION,
  SET_VIEW_ONLY,
  SET_DRAG_DROP_MODE,
  SET_DRAG_ITEM,
  MOVE_ATTRIBUTE,
  CREATE_COUNT_GROUP,
  REMOVE_COUNT_GROUP,
  SET_DROPPED_ATTRIBUTES_IN_TD
} from './actions'
import { find, findIndex, uniqueId, flatten, partial, includes, cloneDeep } from 'lodash'
// TODO we might want to keep this in the targetDefinition if it is needed.
import {COMMANDS} from '@/appLogic/targetDefinition/attributeActionConstants'
// TODO need to get ri of the attribute types here
import types from '@/appComponents/workbench/attributePanel/services/attributeType'

/**
 * target Definition is created from the state.definition.
 * A getter called serializedDefinition is created from the state.definition.
 * this will be used to create the target definition query.
 */

const mutations = {
  [SET_DRAG_DROP_MODE] (state, value) {
    state.dragAndDrop.active = value
  },
  [SET_DRAG_ITEM] (state, value) {
    state.dragAndDrop.draggingItem = value
  },
  [SET_VIEW_ONLY] (state, value) {
    state.viewOnly = value
  },
  [SET_DEFINITION] (state, definition) {
    state.definition.children = definition
    // add a definition ID to each definition child
    walk(state.definition, createDefinitionId)
  },
  [SET_DROPPED_ATTRIBUTES_IN_TD] (state, definition) {
    let p = walkAllAroundOther(state.definition, {children: definition}, updateRemove)
    if (p) {
      state.updateLasTD = state.updateLasTD + 1
    }
  },
  [ADD_ATTRIBUTE] (state, attribute) {
    // always set boolean type value to true
    if (attribute.type === types.BOOLEAN) {
      attribute.value = true
    }
    state.definition.children.push(createDefaultBooleanOperator(), createDefinitionId(attribute))
    state.definition.children = cleanDefinition(state.definition.children)
    state.lastAttributeCommand = {
      command: COMMANDS.ADD_ATTRIBUTE,
      data: attribute
    }
  },
  [UPDATE_ATTRIBUTE] (state, { attribute, update }) {
    Object.assign(attribute, update)
    state.definition.children = cleanDefinition(state.definition.children)
  },
  [SET_TARGET_HAD_CHANGED] (state) {
    state.lastTimeTargetWasChanged = new Date()
  },
  [UPDATE_OPERATOR] (state, { operator, update }) {
    operator.value = update.value
    state.definition.children = cleanDefinition(state.definition.children)
    state.lastAttributeCommand = {
      command: COMMANDS.CHANGED_OPERATOR,
      data: operator
    }
  },
  [REMOVE_ATTRIBUTE] (state) {
    const dropped = dropParts(
      state.definition.children,
      state.selected.map(partial(findDefinitionPartById, state.definition))
    )
    clearSelected(state)
    state.definition.children = cleanDefinition(state.definition.children)
    state.lastAttributeCommand = {
      command: COMMANDS.REMOVED_ATTRIBUTE,
      data: dropped
    }
  },
  [SELECT_ATTRIBUTE] (state, attr) {
    if (state.dragAndDrop.active) return
    toggleSelected(state, attr.definitionId)
    if (isGroup(attr)) {
      walk(attr, child => {
        if (child.definitionId !== attr.definitionId) removeFromSelected(state, child.definitionId)
      })
    }
    // deselect parent group if applicable
    const { parent } = findIndexParentForDefinitionId(state.definition, attr.definitionId)

    if (isGroup(parent) && /* is not root */ !parent.root) {
      getPath(state.definition, attr.definitionId).forEach(inPath => removeFromSelected(state, inPath.definitionId))
    }
  },
  [CLEAR_SELECTION] (state) {
    state.selected = []
  },
  [GROUP] (state) {
    // where the new group will be placed
    let firstSelectedDefinition = state.selected[0]
    const selectedAttributesAndOperators = flatten(state.selected.slice(1).map(partial(findDefinitionPartWithOperatorById, state.definition)))
    // drop all but the first selected item
    dropParts(state.definition.children, selectedAttributesAndOperators)
    // find the index and parent of the selected item
    const {index: insertAt, parent} = findIndexParentForDefinitionId(state.definition, firstSelectedDefinition)
    const firstDefinitionPartInSelection = parent.children[insertAt]
    selectedAttributesAndOperators.unshift(firstDefinitionPartInSelection)
    // drop the first selected item
    dropDefinitionPart(state.definition.children, firstSelectedDefinition)
    // put the selections into a new group, inserting at the location of the first selection
    parent.children.splice(
      insertAt,
      0,
      createGroup(cleanDefinition(selectedAttributesAndOperators))
    )
    state.definition.children = cleanDefinition(state.definition.children)
    clearSelected(state)
    state.lastAttributeCommand = {
      command: COMMANDS.GROUPED_ATTRIBUTES
    }
  },
  [UNGROUP] (state) {
    state.selected.forEach(selectedId => ungroup(state, selectedId))
    clearSelected(state)
    state.definition.children = cleanDefinition(state.definition.children)
    state.lastAttributeCommand = {
      command: COMMANDS.UNGROUPED_ATTRIBUTES
    }
  },
  [CREATE_COUNT_GROUP] (state, { groupId, mustMatch }) {
    const defPart = findDefinitionPartById(state.definition, groupId)
    Vue.set(defPart, 'mustMatch', mustMatch)
    defPart.children.forEach(part => {
      if (isOperator(part)) part.value = 'OR'
    })
    state.lastAttributeCommand = {
      command: COMMANDS.CREATED_COUNT
    }
  },
  [REMOVE_COUNT_GROUP] (state, groupId) {
    const defPart = findDefinitionPartById(state.definition, groupId)
    defPart.mustMatch = null
    state.lastAttributeCommand = {
      command: COMMANDS.REMOVED_COUNT
    }
  },
  [CLEAR_DEFINITION] (state) {
    state.definition.children = []
    state.lastAttributeCommand = {
      command: COMMANDS.CLEARED
    }
  },
  [MOVE_ATTRIBUTE] (state, { droppedId, moveToId, defType }) {
    // find position of dropped Id
    if (droppedId === moveToId) return
    let droppedDefinitionPart = cloneDeep(first(state.definition.children, attr => attr.definitionId === droppedId))
    dropDefinitionPart(state.definition.children, droppedId)
    addDefinitionPartAt(state.definition, droppedDefinitionPart, moveToId, defType)
    state.definition.children = cleanDefinition(state.definition.children)
    state.lastAttributeCommand = {
      command: COMMANDS.MOVED_ATTRIBUTE
    }
  }
}

export const definitionPartTypes = {
  ATTRIBUTE: 'ATTRIBUTE',
  OPERATOR: 'OPERATOR',
  GROUP_BEGINNING: 'GROUP_BEGINNING',
  GROUP_ENDING: 'GROUP_ENDING',
  DROP_ZONE: 'DROP_ZONE',
  COUNT_GROUP: 'COUNT_GROUP'
}

export function first (tree, predicate) {
  let found
  for (let i = 0; i < tree.length; i++) {
    if (predicate(tree[i])) {
      return tree[i]
    } else if (tree[i].children) {
      found = first(tree[i].children, predicate)
      if (found) break
    }
  }
  return found
}

function walkAllAroundOther (def, other, invoke, parent = null) {
  let i = function (o) {
    let passed = invoke(def, o, parent)
    return passed
  }
  let p = walk(other, i)
  if (def.children) {
    def.children.forEach(child => {
      p = walkAllAroundOther(child, other, invoke, def) || p
    })
  }
  return p
}

function walk (definition, invoke) {
  let p = invoke(definition)
  if (definition.children) {
    definition.children.forEach(child => {
      p = walk(child, invoke) || p
    })
  }
  return p
}

// this will place new definition part just before the locationId definition part
function addDefinitionPartAt (definition, newDefinitionPart, locationId, locationType) {
  if (locationType === definitionPartTypes.DROP_ZONE) {
    definition.children.push(createDefaultBooleanOperator())
    definition.children.push(newDefinitionPart)
    return
  }
  let found = false
  walk(definition, (definitionPart) => {
    if (found) return
    if (definitionPart.hasOwnProperty('children')) {
      let indexToInsert = findIndex(definitionPart.children, (node) => node.definitionId === locationId)
      if (indexToInsert > -1) {
        if (locationType === definitionPartTypes.ATTRIBUTE) {
          definitionPart.children.splice(indexToInsert, 0, newDefinitionPart)
          definitionPart.children.splice(indexToInsert + 1, 0, createDefaultBooleanOperator())
        } else if (locationType === definitionPartTypes.OPERATOR) {
          definitionPart.children.splice(indexToInsert, 0, createDefaultBooleanOperator())
          definitionPart.children.splice(indexToInsert + 1, 0, newDefinitionPart)
        } else {
          if (locationType === definitionPartTypes.GROUP_BEGINNING) {
            definitionPart.children.splice(indexToInsert, 0, newDefinitionPart)
            definitionPart.children.splice(indexToInsert + 1, 0, createDefaultBooleanOperator())
          } else if (locationType === definitionPartTypes.GROUP_ENDING) {
            definitionPart.children[indexToInsert].children.push(createDefaultBooleanOperator())
            definitionPart.children[indexToInsert].children.push(newDefinitionPart)
          }
        }
        found = true
      }
    }
  })
}

export function recursiveFilter (definition, predicate) {
  const filtered = []
  walk(definition, function (node) {
    if (predicate(node)) filtered.push(node)
  })
  return filtered
}

function getPath (definition, id) {
  let path = []
  let found = false
  walk(definition, child => {
    if (found) return
    if (child.children && child.definitionId !== id) path.push(child)
    found = (child.definitionId === id)
  })

  return path.reverse()
}

function toggleSelected (state, definitionId) {
  if (find(state.selected, selected => selected === definitionId)) {
    removeFromSelected(state, definitionId)
  } else { // else its not selected, so select it
    addToSelected(state, definitionId)
  }
}

function removeFromSelected (state, definitionId) {
  const foundIndex = findIndex(state.selected, selected => selected === definitionId)
  if (foundIndex > -1) {
    state.selected.splice(foundIndex, 1)
  }
}

function addToSelected (state, definitionId) {
  let newSelected = [].concat(state.selected).concat(definitionId)
  // order the selected in visual order this is done for grouping
  newSelected = recursiveFilter(state.definition, child => includes(newSelected, child.definitionId)).map(selected => { return selected.definitionId })
  state.selected = newSelected
}

function ungroup (state, groupId) {
  const {index, parent} = findIndexParentForDefinitionId(state.definition, groupId)
  const group = parent.children[index]
  // remove group from parent
  parent.children.splice(index, 1)
  // insert group children into groups parent at groups index
  parent.children.splice(index, 0, ...group.children)
}

function findDefinitionPartWithOperatorById (definition, definitionId) {
  const {index, parent} = findIndexParentForDefinitionId(definition, definitionId)
  const found = []
  if (index > 0) {
    found.push(parent.children[index - 1])
  } else {
    // give default bool operator
    found.push(createDefaultBooleanOperator())
  }
  found.push(parent.children[index])
  return found
}

export function findDefinitionPartById (definition, definitionId) {
  return first(definition.children, attr => attr.definitionId === definitionId)
}

function findIndexParentForDefinitionId (definition, definitionId) {
  const tree = Array.isArray(definition) ? definition : definition.children
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].definitionId === definitionId) return {index: i, parent: definition}
    if (tree[i].children) {
      const found = findIndexParentForDefinitionId(tree[i], definitionId)
      if (found) return found
    }
  }

  return false
}

function dropParts (definition, parts) {
  let dropped = []
  parts.forEach(part => {
    dropped = dropped.concat(dropDefinitionPart(definition, part.definitionId))
  })
  return dropped
}

function clearSelected (state) {
  state.selected = []
}

function dropDefinitionPart (dropFrom, dropAttrId) {
  let dropped = []
  dropFrom.forEach((attr, index) => {
    if (attr.children) {
      dropped = dropped.concat(dropDefinitionPart(attr.children, dropAttrId))
    }
    if (attr.definitionId === dropAttrId) {
      dropped.push(dropFrom.splice(index, 1))
    }
  })
  return dropped
}

/**
 * Clean the definition by removing empty groups, removing extra operators etc.
 * @param definition
 */
function cleanDefinition (definition) {
  let cleanDefParts = []

  // clean definition parts, handles empty and single-element definition parts
  definition.forEach((value) => {
    if (value.children) {
      value.children = cleanDefinition(value.children)
      // if we only have one child in group, only keep the child, remove the group
      if (value.children.length === 1 && !isOperator(value.children[0])) {
        value = value.children[0]
        cleanDefParts.push(value)
      } else if (value.children.length === 0) {
        // remove empty groups operator
        if (isOperator(cleanDefParts[cleanDefParts.length - 1])) {
          cleanDefParts.splice(cleanDefParts.length - 1, 1)
        }
      } else {
        cleanDefParts.push(value)
      }
    } else {
      cleanDefParts.push(value)
    }
  })

  let retDef = []

  // prevents operators in illegal positions
  cleanDefParts.forEach((part, index) => {
    const nextPart = cleanDefParts[index + 1]
    if (!isOperator(part)) {
      retDef.push(part)
      // Only include operators that don't precede another operator and is not in the first position of the definition
    } else if (retDef.length > 0 && nextPart && !isOperator(nextPart)) {
      retDef.push(part)
    }
  })

  return retDef
}

function createGroup (children) {
  return createDefinitionId({
    children
  })
}

function isOperator (maybeOperator) {
  return !!(maybeOperator && maybeOperator.operator === true)
}

export function isGroup (maybeGroup) {
  return !!(maybeGroup && maybeGroup.children)
}

export function isCountGroup (maybeCountGroup) {
  return !!(maybeCountGroup && maybeCountGroup.children && maybeCountGroup.mustMatch)
}

function createDefaultBooleanOperator () {
  return createDefinitionId({
    operator: true,
    value: 'OR'
  })
}

/**
 * Creates a unique definitionId on the provided definition part object
 * @param definitionPart The definition part to create the definitionId on
 * @returns {*}
 */
function createDefinitionId (definitionPart) {
  if (definitionPart.definitionId !== undefined) return definitionPart
  definitionPart.definitionId = uniqueId(Date.now().toString())
  return definitionPart
}

function updateRemove (definitionPart, other, parent) {
  let passed = false
  if (definitionPart.id && other.id && definitionPart.id === other.id && parent) {
    for (let i = 0; i < parent.children.length; i++) {
      let child = parent.children[i]
      if (child.id === other.id && !(child.removed === other.removed)) {
        parent.children[i] = cloneDeep(other)
        parent.children[i].definitionId = child.definitionId
        passed = true
      }
    }
  }
  return passed
}

export default mutations



// WEBPACK FOOTER //
// ./src/app/appComponents/workbench/targetDefinition/store/mutations.js